import bowser from 'bowser';
import classNames from 'classnames';
import { default as NextImage } from 'next/image';
import type { ReactElement, Ref, RefObject } from 'react';
import { useMemo, useRef, useCallback, useState } from 'react';
import type { ImageProps as NextImageProps } from 'next/image';
import styles from './Image.module.scss';
import type { ImageProps, ImageRef } from './Image.data';

import {
  defaultObjectFit,
  defaultLayout,
  defaultLoading,
  defaultPlaceholder,
  defaultQuality,
} from './Image.data';
import { useMobileViewport } from '../../../hooks/useMobileViewport';
import sharedVariables from '../../../data/shared-variable/shared-variables.json';
import ScrollTransition from '../../unlisted/scroll-transition/ScrollTransition';
import type {
  ParallaxConfig,
  TransitionConfig,
} from '../../unlisted/scroll-transition/ScrollTransition.data';
import { getImageSize } from '../../../util/storyblok/image.utils';
import { useClientOnly } from '../../../hooks/useClientOnly';
// import { useScrollProviderContext } from '../../unlisted/ScrollProvider';
// import type { ScrollProviderContextType } from '../../unlisted/ScrollProvider';

export default function Image({
  className,
  innerClassName,
  src,
  srcDesktop,
  objectFit = defaultObjectFit,
  title,
  alt,
  quality = defaultQuality,
  layout = defaultLayout,
  placeholder = defaultPlaceholder,
  loading = defaultLoading,
  width,
  height,
  sizes,
  style,
  transition,
  innerTransition,
  ref,
}: ImageProps & { ref?: Ref<ImageRef> }): ReactElement {
  const isMobileViewport = useMobileViewport(sharedVariables.deviceState.MIN_768, true);
  const localRef = useRef(null);
  const elementRef = (ref || localRef) as RefObject<HTMLDivElement | null>;
  const [isLoaded, setIsLoaded] = useState(false);

  // const { scrollWrapperRef, hasSmoothScroll } =
  //   useScrollProviderContext() as ScrollProviderContextType;

  const isFill = layout === 'fill';

  const nextImageProps = useMemo(
    () => {
      const imageUrl = isMobileViewport ? src : srcDesktop;

      const imageSize = getImageSize(imageUrl as string);
      const resolvedWidth = width || imageSize.width;
      const resolvedHeight = height || imageSize.height;
      const nextImageStyle: React.CSSProperties = {
        ...style,
        objectFit,
      };

      const data: Omit<NextImageProps, 'alt'> & { title?: string } = {
        title,
        sizes,
        quality,
        placeholder,
        loading,
        style: nextImageStyle,

        // blurDataURL: `${imageUrl}/m/5x5`, // don't use this, slow perfomance
        src: `${imageUrl}/m/`,
      };

      if (isFill) {
        data.fill = true;
      } else {
        if (width && height) {
          data.src = `${imageUrl}/m/${width}x${height}`;
        }

        data.width = resolvedWidth;
        data.height = resolvedHeight;

        if (layout === 'responsive') {
          data.style = {
            width: '100%',
            height: 'auto',
            ...nextImageStyle,
          };
        } else if (layout === 'intrinsic') {
          data.style = {
            maxWidth: '100%',
            height: 'auto',
            ...nextImageStyle,
          };
        } else if (layout === 'fixed') {
          data.style = {
            width: resolvedWidth,
            height: resolvedHeight,
            ...nextImageStyle,
          };
        }
      }

      return data;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isMobileViewport, layout, objectFit, sizes, quality, isFill, loading, height, style, width],
  );

  const isClientOnly = useClientOnly();
  const handleImageLoad = useCallback(() => {
    setIsLoaded(true);
  }, []);

  let image = (
    <NextImage
      {...nextImageProps}
      alt={alt ?? ''}
      className={classNames(styles.image)}
      onLoad={handleImageLoad}
    />
  );

  // Safari 13.0 did not support next/image component, need to see from where it does support.
  // todo: Add image-element ourself, probably have to add the width/height spacer ourselves (could use a <canvas width={width} height={height} />. as a spacer orso.)
  if (bowser.safari && parseFloat(`${bowser.version}`) <= 13.2 && isClientOnly) {
    image = (
      // eslint-disable-next-line @next/next/no-img-element
      <img
        className={classNames(styles.image, styles.customImage, {
          [styles.fillHeight]: isFill,
        })}
        alt={alt}
        src={nextImageProps.src as string}
      />
    );
  }

  let template;
  let innerTemplate = image;

  // The InnerContainer scroll Transition
  if (innerTransition?.type) {
    const defaultConfig: TransitionConfig = {};

    if (innerTransition.type === 'parallax') {
      defaultConfig.offset = 30;
    }

    innerTemplate = (
      <ScrollTransition
        as="div"
        ref={elementRef}
        type={innerTransition.type}
        config={{
          ...innerTransition.config,
          ...defaultConfig,
        }}
        className={classNames(styles.innerImage, innerClassName, {
          'abs-fill': isFill,
        })}
      >
        {image}
      </ScrollTransition>
    );
  }

  const containerProps = {
    ref: elementRef,
    className: classNames(styles.imageHolder, className, {
      [styles.innerAnimation]: innerTransition?.type,
      [styles.innerParallax]: innerTransition?.type === 'parallax',
      [styles.isHorizontal]:
        (innerTransition?.config as ParallaxConfig)?.direction === 'horizontal',
      [styles.isVertical]:
        innerTransition?.type === 'parallax' &&
        (innerTransition?.config as ParallaxConfig)?.direction !== 'horizontal',
      [styles.isLoaded]: isLoaded,
    }),
  };

  // The Container scroll Transition
  if (transition?.type) {
    template = (
      <ScrollTransition
        as="div"
        type={transition.type}
        config={transition.config}
        vars={transition.vars}
        {...containerProps}
      >
        {innerTemplate}
      </ScrollTransition>
    );
  } else {
    template = <div {...containerProps}>{innerTemplate}</div>;
  }

  return template;
}
